import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiWarningCircleLight } from 'react-icons/pi';
import { containsEmoji, containsDisallowedCharacters } from '../../../utils/validation/validators';
import { removeEmojis, removeSpecialSymbols } from '../../../utils/validation/sanitizers';

interface TextareaProps {
  id: string;
  name?: string;
  label: string;
  placeholder?: string;
  value?: string;
  rows?: number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  error?: string;
  description?: string;
  italicTxt?: string;
  h?: string;
  icon?: React.ReactNode;
  className?: string;
  allowEmojis?: boolean;
  sanitizeEmojis?: boolean;
  required?: boolean;
}

const Textarea: React.FC<TextareaProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  rows = 4,
  onChange,
  disabled,
  error,
  description,
  italicTxt,
  className,
  h,
  icon,
  allowEmojis = false,
  sanitizeEmojis = true,
  required = false,
}) => {
  const { t } = useTranslation();
  const [emojiError, setEmojiError] = useState<string>();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    // Check for emojis if not allowed
    if (!allowEmojis && sanitizeEmojis && newValue) {
      if (containsEmoji(newValue)) {
        setEmojiError(t('Emojis are not allowed in this field'));
        // Auto-remove emojis
        newValue = removeEmojis(newValue);
      } else if (containsDisallowedCharacters(newValue)) {
        setEmojiError(t('Invalid characters detected'));
        newValue = removeSpecialSymbols(newValue);
      } else {
        setEmojiError(undefined);
      }

      // Update the textarea value to the sanitized version
      e.target.value = newValue;
    }

    // Call the original onChange handler
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className='w-full'>
      {label && (
        <label htmlFor={id} className="block text-gray-700 dark:text-gray-300 mb-2 text-xs">
        {t(label)}
      </label>
      )}
      {(italicTxt || description) && (
        <div className='flex flex-col space-y-1 mb-2'>
      {italicTxt && (
        <p className='text-xs text-gray-500 dark:text-gray-300 text-left italic'>{t(italicTxt)}</p>
      )}
      {description && (
        <p className="text-sm text-gray-700 dark:text-gray-200 text-left">{t(description)}</p>
      )}
            </div>
      )}
      <div className="relative">
        {icon && <div className="absolute left-3 top-2">{icon}</div>}
        <textarea
          id={id}
          name={name}
          className={clsx(
            `block text-xs w-full px-3 py-2 bg-white dark:bg-gray-900 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 transition duration-300 input ${h}`,
            icon ? 'pl-10' : 'pl-3',
            error || emojiError ? 'ring-red-500' : 'ring-grayscale-500',
            'focus:ring-primary-500 dark:focus:ring-primary-700',
            h || 'h-24',
            className
          )}
          placeholder={placeholder}
          value={value}
          rows={rows}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
      {(error || emojiError) && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <PiWarningCircleLight className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span>{emojiError || error as string}</span>
        </div>
      )}
    </div>
  );
};

export default Textarea;
